import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  api,
  type ApiError,
  type GetAgentRunTraceResult,
  type GetAgentSpendResult,
  type ListAgentRunsResult,
} from '@whinerp/client';
import { EmptyState, ErrorState, MetricTile, Num, Panel, SectionLabel, Skeleton } from '@whinerp/ui';
import { formatCount, formatDateTime, formatMoney } from '@whinerp/contracts/format';
import { useFormat, useT } from '../../app/i18n.js';
import { defineScreen } from '../types.js';

/**
 * SC-26 Agent activity.
 *
 * What agents did, who asked, and what it cost. Three things shape it.
 *
 * **The cost is on every row.** Not an aggregate somewhere else: the run that
 * went wrong and the run that cost four times the others are usually the same
 * run, and a list that hid the figure would make somebody export it to find
 * out.
 *
 * **The trace reads as sentences.** Each step carries a summary; the raw
 * payload is behind a disclosure. A trace that is only payloads is a trace
 * nobody reads, which makes explainability a claim rather than a feature.
 *
 * **A failed trace fetch keeps the summary row.** 04_Screens asks for it, and
 * the reason is that the row carries the cost and the outcome - losing those
 * because the detail failed is losing the useful half.
 */

type Runs = ListAgentRunsResult;
type Trace = GetAgentRunTraceResult;

function AgentActivityScreen(): JSX.Element {
  const t = useT();
  const format = useFormat();
  const [selected, setSelected] = useState<string | null>(null);

  const runs = useQuery<Runs, ApiError>({
    queryKey: ['agent-runs'],
    queryFn: () => api.listAgentRuns({ query: {} }),
  });

  /*
   * Spend is its own question and its own request.
   *
   * The run list carries a per-row cost; this is the total, and it loads
   * separately so a slow aggregate over a long period does not hold up the
   * list somebody is reading.
   */
  const spend = useQuery<GetAgentSpendResult, ApiError>({
    queryKey: ['agent-spend'],
    queryFn: () => api.getAgentSpend({ query: {} }),
  });

  // Its own query: a failed trace must not take the list down with it.
  const trace = useQuery<Trace, ApiError>({
    queryKey: ['agent-run-trace', selected],
    queryFn: () => api.getAgentRunTrace({ path: { runId: selected ?? '' } }),
    enabled: selected !== null,
  });

  if (runs.isPending) return <ActivityLoading />;
  if (runs.isError) return <ActivityError error={runs.error} retry={() => void runs.refetch()} />;
  if (runs.data.runs.length === 0) return <ActivityEmpty reason="no-records" />;

  return (
    <div data-component="AgentActivity" className="flex w-full min-w-0 flex-col gap-3">
      <header className="flex flex-wrap items-end gap-4">
        <SectionLabel>{t('agent.activity')}</SectionLabel>
        <span className="ms-auto">
          {spend.isPending ? (
            <Skeleton className="h-line-h1 w-panel-narrow" />
          ) : (
            <MetricTile
              data-metric="spend"
              label={t('agent.spend')}
              value={formatMoney(spend.data?.amount ?? '0.00', format, {
                currency: spend.data?.currency ?? 'USD',
              })}
              context={t('agent.tokens', {
                values: {
                  input: formatCount(Number(spend.data?.inputTokens ?? 0), format),
                  output: formatCount(Number(spend.data?.outputTokens ?? 0), format),
                },
              })}
              labels={{ up: (c) => c, down: (c) => c, flat: '' }}
            />
          )}
        </span>
      </header>

      {/* The ceiling, beside the figure approaching it.
          A spend number on its own answers "how much" and not "is that a
          lot", which is the only question anybody actually has. */}
      {(spend.data?.budgets ?? []).map((budget) => (
        <Panel key={budget.budgetId} data-budget={budget.scope} data-budget-state={budget.state}>
          <div className="flex flex-col gap-1">
            <div className="flex flex-wrap items-baseline gap-2">
              <SectionLabel>{t('agent.budget')}</SectionLabel>
              <span className="text-meta text-ink-meta">
                {t('agent.budget.ofLimit', {
                  values: {
                    spent: formatMoney(budget.spent, format, { currency: budget.currency }),
                    limit: formatMoney(budget.limit, format, { currency: budget.currency }),
                  },
                })}
              </span>
              {/* The state in words, and never only as a colour on a bar.
                  A red bar in a printed pack is a grey bar. */}
              <span
                data-budget-label
                className={
                  budget.state === 'exceeded'
                    ? 'rounded-chip bg-danger-tint px-1.5 text-meta text-danger-text'
                    : budget.state === 'warning'
                      ? 'rounded-chip bg-warning-tint px-1.5 text-meta text-warning-text'
                      : 'rounded-chip bg-sunken px-1.5 text-meta text-ink-600'
                }
              >
                {t(`agent.budget.${budget.state}` as 'agent.budget.ok')}
              </span>
              {/* Whether this stops anything, said out loud. A limit that only
                  reports and a limit that refuses look identical on a bar, and
                  they are entirely different promises. */}
              <span className="ms-auto text-meta text-ink-meta">
                {budget.hardStop ? t('agent.budget.stops') : t('agent.budget.reports')}
              </span>
            </div>
            <div className="h-1.5 w-full overflow-hidden rounded-rail bg-sunken" aria-hidden>
              <div
                className={
                  budget.state === 'exceeded'
                    ? 'h-full bg-danger'
                    : budget.state === 'warning'
                      ? 'h-full bg-warning'
                      : 'h-full bg-bronze'
                }
                style={{ inlineSize: `${String(Math.min(budget.percent, 100))}%` }}
              />
            </div>
          </div>
        </Panel>
      ))}

      {/* Where the money went. A total says whether the workspace is over; it
          never says who, and the second is what changes anybody's behaviour. */}
      {(spend.data?.byAgent.length ?? 0) > 0 ? (
        <Panel data-region="chargeback">
          <div className="flex flex-col gap-2">
            <SectionLabel>{t('agent.spend.who')}</SectionLabel>
            <div className="flex flex-wrap gap-6">
              {(['byAgent', 'byUser'] as const).map((dimension) => (
                <div key={dimension} data-breakdown={dimension} className="min-w-0 flex-1">
                  <p className="pb-1 text-micro uppercase tracking-label text-ink-label">
                    {t(dimension === 'byAgent' ? 'agent.spend.byAgent' : 'agent.spend.byUser')}
                  </p>
                  <ul className="flex flex-col">
                    {(spend.data?.[dimension] ?? []).map((row) => (
                      <li
                        key={row.key}
                        data-spend-row={row.key}
                        className="flex items-baseline gap-3 border-t border-t-rule py-1 first:border-t-0"
                      >
                        <span className="min-w-0 flex-1 truncate text-body text-ink">
                          {row.label || t('agent.spend.unnamed')}
                        </span>
                        <Num className="shrink-0 text-meta text-ink-meta">
                          {t('agent.spend.runs')} {formatCount(row.runs, format)}
                        </Num>
                        <Num data-spend-amount className="shrink-0 text-body text-ink">
                          {formatMoney(row.amount, format, {
                            currency: spend.data?.currency ?? 'USD',
                          })}
                        </Num>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </Panel>
      ) : null}

      <div className="flex min-w-0 gap-4">
        <section aria-label={t('agent.activity')} data-pane="runs" className="flex min-w-0 flex-1 flex-col gap-1">
          <ul className="flex flex-col gap-1">
            {runs.data.runs.map((run) => (
              <li key={run.runId}>
                <button
                  type="button"
                  data-run={run.runId}
                  aria-pressed={selected === run.runId}
                  onClick={() => setSelected(run.runId)}
                  className="flex w-full flex-col gap-0.5 rounded-card border border-rule p-2 text-start hover:bg-sunken"
                >
                  <span className="flex flex-wrap items-baseline gap-2">
                    <span className="text-body text-ink">{run.agentName}</span>
                    <span data-run-status={run.status} className="text-meta text-ink-meta">
                      {run.outcome ?? run.status}
                    </span>
                    {/* On the row. The run that went wrong and the run that
                        cost four times the others are usually the same run. */}
                    <Num data-run-cost={run.runId} className="ms-auto text-meta text-ink">
                      {formatMoney(run.amount, format, { currency: run.currency })}
                    </Num>
                  </span>
                  <span className="text-meta text-ink-meta">
                    {t('agent.invokedBy')} {run.invokedByName ?? ''} ·{' '}
                    {formatDateTime(run.startedAt, format)}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </section>

        <section aria-label={t('agent.trace')} data-pane="trace" className="flex w-panel-wide shrink-0 flex-col gap-2">
          <SectionLabel>{t('agent.trace')}</SectionLabel>
          {selected === null ? (
            <p className="text-meta text-ink-meta">{t('agent.run')}</p>
          ) : trace.isPending ? (
            <Skeleton className="h-plot w-full" />
          ) : trace.isError ? (
            /* The summary row stays: it carries the cost and the outcome, and
               losing those because the detail failed loses the useful half. */
            <p role="alert" data-trace-failed className="text-dense text-danger-text">
              {trace.error.message}
            </p>
          ) : (
            <Panel>
              <ol data-trace className="flex flex-col gap-2">
                {trace.data.steps.map((step) => (
                  <li key={step.sequence} data-trace-step={step.kind} className="flex flex-col gap-0.5">
                    <span className="text-micro uppercase tracking-label text-ink-label">
                      {t(`agent.step.${step.kind}` as 'agent.step.thought')}
                    </span>
                    {/* A sentence, always. A trace of payloads is a trace
                        nobody reads, which makes explainability a claim. */}
                    <span className="text-body text-ink">{step.summary}</span>
                    {step.payload ? (
                      <details data-raw-payload={String(step.sequence)}>
                        <summary className="cursor-pointer text-meta text-ink-meta">
                          {t('agent.rawPayload')}
                        </summary>
                        <pre className="overflow-x-auto text-meta text-ink-meta" data-scroll-container>
                          {JSON.stringify(step.payload, null, 2)}
                        </pre>
                      </details>
                    ) : null}
                  </li>
                ))}
              </ol>
            </Panel>
          )}
        </section>
      </div>
    </div>
  );
}

function ActivityEmpty({
  reason,
}: {
  reason: 'no-records' | 'filtered-to-nothing' | 'permission-denied';
}): JSX.Element {
  const t = useT();
  return (
    <EmptyState
      reason={reason}
      illustration="Sparkle"
      title={t('agent.activity')}
      body={t('agent.activity.empty')}
      primary={{ label: t('empty.action.openAgents'), icon: 'Sparkle', href: '/agents/proposals' }}
    />
  );
}

function ActivityLoading(): JSX.Element {
  const t = useT();
  return (
    <div className="flex w-full gap-4" role="status" aria-live="polite" aria-busy>
      <span className="sr-only">{t('state.loading')}</span>
      <Skeleton className="h-plot flex-1" />
      <Skeleton className="h-plot w-panel-wide" />
    </div>
  );
}

function ActivityError({ error, retry }: { error: unknown; retry: () => void }): JSX.Element {
  return (
    <ErrorState
      title="Agent activity could not be loaded"
      body={error instanceof Error ? error.message : 'The run list did not load.'}
      onRetry={retry}
    />
  );
}

export const screen = defineScreen({
  id: 'SC-26',
  route: '/agents/activity',
  surface: 'page',
  title: 'Agent activity',
  features: ['F-112', 'F-115'],
  entitlement: 'F-112',
  reachedBy: [{ kind: 'nav', item: 'agents' }],
  components: ['MetricTile'],
  elements: ['AgentActivity'],
  states: { empty: ActivityEmpty, loading: ActivityLoading, error: ActivityError },
  component: AgentActivityScreen,
});

export default screen;
